import { useRef, useState } from "react";
import type { ChangeEvent, DragEvent, FormEvent } from "react";
import {
  hasAllowedFileExtension,
  MAX_FILE_BYTES,
  orderSchema,
  serviceOptions,
} from "../lib/order-schema";
import { siteConfig } from "../site-config";
import { ArrowRightIcon, CheckIcon, LockIcon, UploadIcon } from "./Icons";

type SubmissionState = "idle" | "submitting" | "success" | "error";
type FieldErrors = Partial<Record<"name" | "whatsapp" | "email" | "service" | "description", string>>;

const fileAccept = ".stl,.obj,.step,.stp,.zip,.jpg,.jpeg,.png,.pdf";

function getString(formData: FormData, fieldName: string) {
  const value = formData.get(fieldName);
  return typeof value === "string" ? value : "";
}

function formatFileSize(sizeInBytes: number) {
  if (sizeInBytes < 1024 * 1024) {
    return `${Math.ceil(sizeInBytes / 1024)} KB`;
  }

  return `${(sizeInBytes / 1024 / 1024).toFixed(1)} MB`;
}

function validateAttachment(file: File | null) {
  if (!file) {
    return "";
  }

  if (file.size > MAX_FILE_BYTES) {
    return "Ukuran file maksimal 20 MB.";
  }

  if (!hasAllowedFileExtension(file.name)) {
    return "Format file belum didukung.";
  }

  return "";
}

export function OrderForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const startedAtRef = useRef(String(Date.now()));
  const [attachment, setAttachment] = useState<File | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [fileError, setFileError] = useState("");
  const [submissionState, setSubmissionState] = useState<SubmissionState>("idle");
  const [submissionMessage, setSubmissionMessage] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  const selectAttachment = (file: File | null) => {
    const validationMessage = validateAttachment(file);
    setFileError(validationMessage);
    setAttachment(validationMessage ? null : file);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    selectAttachment(event.target.files?.[0] ?? null);
  };

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
    selectAttachment(event.dataTransfer.files?.[0] ?? null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const requestData = new FormData(form);
    const values = {
      name: getString(requestData, "name"),
      whatsapp: getString(requestData, "whatsapp"),
      email: getString(requestData, "email"),
      service: getString(requestData, "service"),
      description: getString(requestData, "description"),
      website: getString(requestData, "website"),
      startedAt: startedAtRef.current,
    };
    const validation = orderSchema.safeParse(values);
    const attachmentError = siteConfig.attachmentsEnabled ? validateAttachment(attachment) : "";

    if (!validation.success || attachmentError) {
      const nextErrors: FieldErrors = {};
      if (!validation.success) {
        const flattenedErrors = validation.error.flatten().fieldErrors;
        for (const fieldName of Object.keys(flattenedErrors) as Array<keyof FieldErrors>) {
          nextErrors[fieldName] = flattenedErrors[fieldName]?.[0];
        }
      }

      setFieldErrors(nextErrors);
      setFileError(attachmentError);
      setSubmissionState("error");
      setSubmissionMessage("Periksa kembali data yang belum lengkap.");
      return;
    }

    requestData.set("startedAt", startedAtRef.current);
    requestData.delete("attachment");
    if (siteConfig.attachmentsEnabled && attachment) {
      requestData.append("attachment", attachment, attachment.name);
    }

    setFieldErrors({});
    setFileError("");
    setSubmissionState("submitting");
    setSubmissionMessage("");

    try {
      const response = await fetch("/api/orders", {
        method: "POST",
        body: requestData,
        headers: {
          Accept: "application/json",
        },
      });
      const result = (await response.json()) as { message?: string };

      if (!response.ok) {
        throw new Error(result.message || "Permintaan belum dapat dikirim.");
      }

      formRef.current?.reset();
      setAttachment(null);
      startedAtRef.current = String(Date.now());
      setSubmissionState("success");
      setSubmissionMessage("Terima kasih. Tim Morolab akan menghubungimu maksimal 1 hari kerja.");
    } catch (error) {
      setSubmissionState("error");
      setSubmissionMessage(error instanceof Error ? error.message : "Terjadi kendala. Coba lagi sebentar.");
    }
  };

  return (
    <div className="order-form-shell">
      <form className="order-form" onSubmit={handleSubmit} ref={formRef} noValidate>
        <div className="honeypot" aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input autoComplete="off" id="website" name="website" tabIndex={-1} />
        </div>

        <div className="field-grid">
          <div className="field">
            <label htmlFor="name">Nama</label>
            <input aria-describedby={fieldErrors.name ? "name-error" : undefined} aria-invalid={Boolean(fieldErrors.name)} id="name" name="name" placeholder="Nama lengkap" />
            {fieldErrors.name ? <span className="field-error" id="name-error">{fieldErrors.name}</span> : null}
          </div>

          <div className="field">
            <label htmlFor="whatsapp">WhatsApp</label>
            <input aria-describedby={fieldErrors.whatsapp ? "whatsapp-error" : undefined} aria-invalid={Boolean(fieldErrors.whatsapp)} id="whatsapp" inputMode="tel" name="whatsapp" placeholder="08xx xxxx xxxx" />
            {fieldErrors.whatsapp ? <span className="field-error" id="whatsapp-error">{fieldErrors.whatsapp}</span> : null}
          </div>

          <div className="field">
            <label htmlFor="email">Email</label>
            <input aria-describedby={fieldErrors.email ? "email-error" : undefined} aria-invalid={Boolean(fieldErrors.email)} id="email" inputMode="email" name="email" placeholder="nama@email.com" type="email" />
            {fieldErrors.email ? <span className="field-error" id="email-error">{fieldErrors.email}</span> : null}
          </div>

          <div className="field">
            <label htmlFor="service">Jenis layanan</label>
            <select aria-describedby={fieldErrors.service ? "service-error" : undefined} aria-invalid={Boolean(fieldErrors.service)} defaultValue="" id="service" name="service">
              <option disabled value="">Pilih layanan</option>
              {serviceOptions.map((service) => (
                <option key={service.value} value={service.value}>{service.label}</option>
              ))}
            </select>
            {fieldErrors.service ? <span className="field-error" id="service-error">{fieldErrors.service}</span> : null}
          </div>
        </div>

        <div className="field">
          <label htmlFor="description">Ceritakan proyekmu</label>
          <textarea
            aria-describedby={fieldErrors.description ? "description-error" : undefined}
            aria-invalid={Boolean(fieldErrors.description)}
            id="description"
            name="description"
            placeholder="Ukuran, jumlah, fungsi, deadline, atau detail lain yang sudah kamu punya."
            rows={5}
          />
          {fieldErrors.description ? <span className="field-error" id="description-error">{fieldErrors.description}</span> : null}
        </div>

        {siteConfig.attachmentsEnabled ? (
          <div className="field file-field">
            <span className="field-label">Lampirkan file (opsional)</span>
            <label
              className={isDragging ? "file-drop is-dragging" : "file-drop"}
              htmlFor="attachment"
              onDragEnter={() => setIsDragging(true)}
              onDragLeave={() => setIsDragging(false)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={handleDrop}
            >
              <UploadIcon />
              <span className="file-drop-title">
                {attachment ? attachment.name : "Klik untuk mengunggah atau seret file ke sini"}
              </span>
              <span className="file-drop-help">
                {attachment ? formatFileSize(attachment.size) : "STL, OBJ, STEP, ZIP, JPG, PNG, atau PDF • maks. 20 MB"}
              </span>
              <input accept={fileAccept} id="attachment" name="attachment" onChange={handleFileChange} ref={inputRef} type="file" />
            </label>
            {attachment ? (
              <button className="file-remove" onClick={() => {
                setAttachment(null);
                if (inputRef.current) inputRef.current.value = "";
              }} type="button">
                Hapus lampiran
              </button>
            ) : null}
            {fileError ? <span className="field-error">{fileError}</span> : null}
          </div>
        ) : (
          <div className="field file-field">
            <span className="field-label">Lampiran file</span>
            <div className="file-drop is-disabled" role="note">
              <UploadIcon />
              <span className="file-drop-title">Lampiran dapat dikirim setelah permintaan masuk</span>
              <span className="file-drop-help">Tim Morolab akan menghubungimu untuk menerima file desain.</span>
            </div>
          </div>
        )}

        <button className="button button-primary submit-button" disabled={submissionState === "submitting"} type="submit">
          {submissionState === "submitting" ? "Mengirim..." : "Kirim Permintaan"}
          <ArrowRightIcon />
        </button>

        {siteConfig.whatsappUrl ? (
          <a className="button button-outline whatsapp-button" href={siteConfig.whatsappUrl} rel="noreferrer" target="_blank">
            Atau konsultasi cepat via WhatsApp
            <ArrowRightIcon />
          </a>
        ) : (
          <span className="button button-outline whatsapp-button is-unconfigured" title="Tambahkan VITE_WHATSAPP_NUMBER sebelum deploy">
            Atau konsultasi cepat via WhatsApp
            <ArrowRightIcon />
          </span>
        )}

        <p className="privacy-note"><LockIcon /> Data hanya digunakan untuk membalas kebutuhan proyekmu.</p>
        {submissionState === "error" ? <p className="form-message form-message-error" role="alert">{submissionMessage}</p> : null}
      </form>

      {submissionState === "success" ? (
        <div className="success-panel" role="status">
          <span className="success-icon"><CheckIcon /></span>
          <h3>Permintaan terkirim.</h3>
          <p>{submissionMessage}</p>
          <button className="text-link" onClick={() => setSubmissionState("idle")} type="button">
            Kirim permintaan lain
            <ArrowRightIcon />
          </button>
        </div>
      ) : null}
    </div>
  );
}
